/**
 * Soru → ders / ünite / Zihin Ağı düğümü eşleştirmesi (saf fonksiyonlar, Firebase yok).
 * services/userService.ts buradan yeniden dışa aktarır; eşleştirme denetimi: scripts/audit-topic-routing.ts
 */
import { SubjectType } from '../types';
import { HISTORY_CHRONOLOGY } from '../data/history/chronology';
import { TOPICS_BY_SUBJECT } from '../data/topics';
import { EXAM_BLUEPRINTS } from '../data/topicDistributions';

// Resmî ünite adı → ders (EXAM_BLUEPRINTS). Ders tespitinde ilk bakılan yer: tohumlanan ünite adı kesin derstir.
const UNIT_TO_SUBJECT = new Map<string, SubjectType>();
for (const [subject, blueprint] of Object.entries(EXAM_BLUEPRINTS) as [SubjectType, typeof EXAM_BLUEPRINTS[SubjectType]][]) {
    for (const bp of blueprint) {
        for (const unit of [bp.topic, ...(bp.topics || [])]) UNIT_TO_SUBJECT.set(unit.toLocaleLowerCase('tr-TR').trim(), subject);
    }
}

// --- YARDIMCI FONKSİYONLAR ---

// Türkçe karakterleri İngilizceye çevirip güvenli ID yapar
export const slugify = (text: string): string => {
    return text
        .toLocaleLowerCase('tr-TR')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]/g, '_') // Harf/rakam olmayan her şeyi alt tire yap
        .replace(/_+/g, '_'); // Üst üste gelen alt tireleri teke düşür
};

// Soru metninden ve konusundan benzersiz ID üret
export const generateQuestionId = (text: string, topic?: string): string => {
    const cleanText = (text || '').trim().toLocaleLowerCase('tr-TR').replace(/\s+/g, '');
    let hash = 0;
    for (let i = 0; i < cleanText.length; i++) {
        const char = cleanText.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // 32bit integer
    }
    const hashStr = Math.abs(hash).toString();
    const prefix = topic ? slugify(topic) : 'genel';
    return `${prefix}_${hashStr}`;
};

export const TOPIC_TO_NODE_MAP: Record<string, string> = {
    // Coğrafya (9)
    "Türkiye'nin Fiziki Özellikleri": "fiziki_ozellikler",
    "Türkiye'nin Coğrafi Konumu": "cografi_konum",
    "Türkiye'nin İklimi ve Bitki Örtüsü": "iklim",
    "Türkiye'de Nüfus ve Yerleşme": "nufus",
    "Türkiye'de Tarım": "tarim",
    "Türkiye'de Hayvancılık": "hayvancilik",
    "Türkiye'de Madenler ve Enerji Kaynakları": "madenler_enerji",
    "Türkiye'de Sanayi": "sanayi",
    "Türkiye'de Ulaşım, Ticaret ve Turizm": "ulasim_ticaret",
    // Vatandaşlık (8)
    "Hukukun Temel Kavramları": "cit_hukuk_temel",
    "Türk Anayasa Tarihi": "cit_anayasa_tarihi",
    "1982 Anayasası ve Temel Esaslar": "cit_1982",
    "Temel Hak ve Hürriyetler": "cit_haklar",
    "Yasama": "cit_yasama",
    "Yürütme": "cit_yurutme",
    "Yargı": "cit_yargi",
    "İdare Hukuku": "cit_idare",
    "Uluslararası Kuruluşlar": "cit_guncel",
    "Güncel Bilgiler": "cit_guncel",
    // Türkçe (13) & Alt Konu Eşleştirmeleri
    "Sözcükte Anlam": "trk_sozcuk_anlam",
    "Sözcüğün ve Sözün Anlamı": "trk_sozcuk_anlam",
    "Sözcüğün Anlam Açılımı (Gerçek, Mecaz, Terim)": "trk_sozcuk_anlam",
    "Sözcükler Arası Anlam İlişkileri": "trk_sozcuk_anlam",
    "Sözcüğe Mecaz Anlam Kazandırma Yolları": "trk_sozcuk_anlam",
    "Söz Öbekleri (Deyimler, Atasözleri, İkilemeler)": "trk_sozcuk_anlam",
    "Cümlede Anlam": "trk_cumle_anlam",
    "Cümlenin Anlamı": "trk_cumle_anlam",
    "Anlamlarına Göre Cümleler": "trk_cumle_anlam",
    "Anlam İlişkilerine Göre Cümleler (Neden, Amaç, Koşul)": "trk_cumle_anlam",
    "Cümlenin Niteliği (Öznel - Nesnel Anlatım)": "trk_cumle_anlam",
    "Bir Duygunun, Düşüncenin, Kavramın Yer Aldığı Cümleler": "trk_cumle_anlam",
    "Cümlede Boşluk Tamamlama": "trk_cumle_anlam",
    "Cümleden Kesin Yargı Çıkarma": "trk_cumle_anlam",
    "Cümle Oluşturma": "trk_cumle_anlam",
    "Paragrafta Anlam ve Yapı": "trk_paragraf",
    "Paragraf": "trk_paragraf",
    "Paragrafın Yapısı ve Anlamı": "trk_paragraf",
    "Paragrafta Yapı (Akışı Bozan Cümle, Paragraf Bölme, Tamamlama)": "trk_paragraf",
    "Paragrafta Ana Düşünce ve Konu": "trk_paragraf",
    "Paragrafta Yardımcı Düşünceler": "trk_paragraf",
    "Paragrafta Başlık ve Soruya Karşılık Olma": "trk_paragraf",
    "Ses Bilgisi": "trk_ses",
    "Ünsüzlerle İlgili Ses Olayları": "trk_ses",
    "Ünlülerle İlgili Ses Olayları": "trk_ses",
    "Yazım Kuralları": "trk_yazim",
    "İkilemeler ve Deyimlerin Yazılışı": "trk_yazim",
    "Pekiştirmeli Sözlerin Yazılışı": "trk_yazim",
    '"mı, mi" Soru Ekinin Yazılışı': "trk_yazim",
    '"da, de" Bağlacı ve Hâl Ekinin Yazılışı': "trk_yazim",
    '"ki" Bağlacının ve "-ki" Ekinin Yazılışı': "trk_yazim",
    "Sayıların ve Tarihlerin Yazılışı": "trk_yazim",
    "Kısaltmaların Yazılışı": "trk_yazim",
    "Büyük Harflerin Kullanımı": "trk_yazim",
    "Birleşik Sözcüklerin Yazılışı": "trk_yazim",
    "Düzeltme İşaretinin (^) Kullanımı": "trk_yazim",
    "Noktalama İşaretleri": "trk_noktalama",
    "Nokta, Virgül ve Noktalı Virgül": "trk_noktalama",
    "İki Nokta ve Üç Nokta": "trk_noktalama",
    "Soru ve Ünlem İşareti": "trk_noktalama",
    "Kısa Çizgi ve Uzun Çizgi": "trk_noktalama",
    "Tırnak ve Tek Tırnak İşareti": "trk_noktalama",
    "Yay Ayraç, Köşeli Ayraç ve Kesme İşareti": "trk_noktalama",
    "Sözcükte Yapı": "trk_sozcuk_yapi",
    "Biçim Bilgisi": "trk_sozcuk_yapi",
    "Yapı Bilgisi (Kök, Ek ve Sözcüğün Yapısı)": "trk_sozcuk_yapi",
    "Sözcük Türleri": "trk_sozcuk_tur",
    "Sözcük Türleri (İsim, Sıfat, Zamir, Zarf)": "trk_sozcuk_tur",
    "Sözcük Türleri (Edat, Bağlaç, Ünlem, Fiil, Ek Fiil, Fiilimsi)": "trk_sozcuk_tur",
    "Fiiller ve Fiilimsiler": "trk_fiil",
    "Çatı Bilgisi (Fiilde Çatı)": "trk_fiil",
    "Cümlenin Ögeleri": "trk_oge",
    "Cümlenin Ögeleri - Cümle Türleri": "trk_oge",
    "Cümle Türleri": "trk_cumle_tur",
    "Cümle Çeşitleri (Cümle Türleri)": "trk_cumle_tur",
    "Anlatım Bozuklukları": "trk_anlatim",
    "Anlatım Bilgileri": "trk_anlatim",
    "Anlamdan Kaynaklanan Anlatım Bozuklukları": "trk_anlatim",
    "Dil Bilgisinden Kaynaklanan Anlatım Bozuklukları": "trk_anlatim",
    "Anlatım Özellikleri (Açıklık, Akıcılık, Yalınlık, Duruluk)": "trk_anlatim",
    "Anlatım Biçimleri (Öyküleme, Betimleme, Açıklama, Tartışma)": "trk_anlatim",
    "Düşünceyi Geliştirme Yolları (Tanımlama, Karşılaştırma, Örnekleme vb.)": "trk_anlatim",
    "Sözel Mantık": "trk_sozel_mantik",
    "Sıralama ve Mekânda Yerleştirme Senaryoları": "trk_sozel_mantik",
    "Grup ve Eşleştirme Senaryoları": "trk_sozel_mantik",
    "Üç ve Çok Bilinmeyenli Matris Senaryoları": "trk_sozel_mantik",
    "Rota, Ağ ve Karmaşık Mantık Senaryoları": "trk_sozel_mantik",
    // Matematik (29 Nöron & Alt Konular)
    "Temel Kavramlar": "mat_temel",
    "Tek ve Çift Sayılar": "mat_temel",
    "Asal Sayılar": "mat_temel",
    "Ardışık Sayılar": "mat_temel",
    "Faktöriyel": "mat_temel",
    "Pozitif - Negatif Sayılar": "mat_temel",
    "Basamak Analizi": "mat_basamak",
    "Sayı Basamakları": "mat_basamak",
    "Bölme - Bölünebilme": "mat_bolme",
    "Bölme ve Bölünebilme": "mat_bolme",
    "Ebob - Ekok": "mat_ebob",
    "EBOB - EKOK": "mat_ebob",
    "Rasyonel Sayılar": "mat_rasyonel",
    "Ondalıklı Sayılar": "mat_ondalik",
    "Basit Eşitsizlikler": "mat_esitsizlik",
    "Pozitif - Negatif Sayılar ve Basit Eşitsizlikler": "mat_esitsizlik",
    "Mutlak Değer": "mat_mutlak",
    "Üslü Sayılar": "mat_uslu",
    "Üslü İfadeler": "mat_uslu",
    "Köklü Sayılar": "mat_koklu",
    "Köklü İfadeler": "mat_koklu",
    "Çarpanlara Ayırma": "mat_carpanlar",
    "Denklem Çözme": "mat_denklem",
    "Birinci Dereceden Denklemler": "mat_denklem",
    "Oran Orantı": "mat_oran",
    "Oran - Orantı": "mat_oran",
    "Kümeler": "mat_kume",
    "Sayı - Kesir Problemleri": "mat_sayi_prob",
    "Sayı Problemleri": "mat_sayi_prob",
    "Kesir Problemleri": "mat_sayi_prob",
    "Yaş Problemleri": "mat_yas",
    "Yüzde Problemleri": "mat_yuzde",
    "Yüzde - Kar - Zarar Problemleri": "mat_yuzde",
    "Kar - Zarar Problemleri": "mat_kar_zarar",
    "Kar-Zarar": "mat_kar_zarar",
    "Karışım Problemleri": "mat_karisim",
    "Karışım Problemi": "mat_karisim",
    "Hareket Problemleri": "mat_hareket",
    "İşçi Problemleri": "mat_isci",
    "İşçi - Havuz Problemleri": "mat_isci",
    "Fonksiyonlar": "mat_fonksiyon",
    "Periyodik Problemler": "mat_periyodik",
    "İşlem & Modüler Aritmetik": "mat_islem",
    "İşlem": "mat_islem",
    "Modüler Aritmetik": "mat_islem",
    "Özel Sayılar": "mat_ozel_sayilar",
    "Permütasyon-Kombinasyon-Olasılık": "mat_permutasyon",
    "Permütasyon - Kombinasyon - Olasılık": "mat_permutasyon",
    "Permütasyon": "mat_permutasyon",
    "Kombinasyon": "mat_permutasyon",
    "Olasılık": "mat_permutasyon",
    "Sayısal Mantık": "mat_mantik",
    "Tablo & Grafik Problemleri": "mat_grafik",
    "Grafik Problemleri": "mat_grafik",
    "Geometri": "mat_geometri",
    "Doğruda ve Üçgende Açılar": "mat_geometri",
    "Özel Üçgenler": "mat_geometri",
    "Açıortay - Kenarortay": "mat_geometri",
    "Üçgende Benzerlik": "mat_geometri",
    "Üçgende Alan": "mat_geometri",
    "Çokgenler ve Dörtgenler": "mat_geometri",
    "Çember ve Daire": "mat_geometri",
    "Analitik Geometri": "mat_geometri",
    "Katı Cisimler": "mat_geometri",
    // Müfredat (EXAM_BLUEPRINTS) adları — eksikken bu ünitelerin hataları Zihin Ağı'nda olmayan düğümlere
    // (mat_dortgenler, mat_cember_daire, mat_tablo_grafik_problemi...) yazılıyor ve hiç görünmüyordu.
    "Dik, İkizkenar ve Eşkenar Üçgen": "mat_geometri",
    "Açıortay ve Kenarortay": "mat_geometri",
    "Üçgende Alan ve Benzerlik": "mat_geometri",
    "Çokgenler": "mat_geometri",
    "Dörtgenler": "mat_geometri",
    "Çember - Daire": "mat_geometri",
    "Tablo Grafik Problemi": "mat_grafik"
};

// --- DİNAMİK DERS TESPİT YARDIMCISI ---
export const getSubjectFromTopic = (topic: string): SubjectType | null => {
    if (!topic) return null;
    const normalized = topic.trim().toLocaleLowerCase('tr-TR');

    // 0. Resmî ünite adıysa dersi kesindir
    const unitSubject = UNIT_TO_SUBJECT.get(normalized);
    if (unitSubject) return unitSubject;

    // 1. Birebir veya tam eşleşme ara
    for (const [sub, topics] of Object.entries(TOPICS_BY_SUBJECT)) {
        if (topics.some(t => t.toLocaleLowerCase('tr-TR') === normalized)) {
            return sub as SubjectType;
        }
    }
    
    // 2. Alt kelime eşleşmesi ara (içeriyor mu kontrolü)
    for (const [sub, topics] of Object.entries(TOPICS_BY_SUBJECT)) {
        if (topics.some(t => t.toLocaleLowerCase('tr-TR').includes(normalized) || normalized.includes(t.toLocaleLowerCase('tr-TR')))) {
            return sub as SubjectType;
        }
    }

    // 3. 🛡️ GÜÇLÜ ANAHTAR KELİME TARAMASI (Subject Keywords):
    const t = normalized;
    // Tarih anahtar kelimeleri:
    if (
        t.includes("osmanlı") || t.includes("selçuklu") || t.includes("türk dünyası") ||
        t.includes("islamiyet") || t.includes("beylik") || t.includes("meşrutiyet") ||
        t.includes("tanzimat") || t.includes("ıslahat") || t.includes("dağılma") ||
        t.includes("gerileme") || t.includes("duraklama") || t.includes("yükselme") ||
        t.includes("kurtuluş savaşı") || t.includes("millî mücadele") || t.includes("milli mücadele") ||
        t.includes("atatürk") || t.includes("tbmm") || t.includes("lozan") ||
        t.includes("mondros") || t.includes("sevr") || t.includes("kongre") ||
        t.includes("inkılap") || t.includes("çağdaş türk") || t.includes("padişah") ||
        t.includes("antlaşma") || t.includes("isyan") || t.includes("fetret") ||
        t.includes("divan") || t.includes("sadrazam") || t.includes("yeniçeri") ||
        t.includes("denge politikası") || t.includes("kapitülasyon") || t.includes("sultan") ||
        t.includes("hükümdar") || t.includes("muharebe") || t.includes("cephe") ||
        t.includes("kronoloji") || t.includes("mudanya") || t.includes("amasya") ||
        t.includes("erzurum") || t.includes("sivas") || t.includes("sakarya")
    ) {
        return 'history';
    }

    // Vatandaşlık anahtar kelimeleri:
    if (
        t.includes("anayasa") || t.includes("hukuk") || t.includes("yasama") ||
        t.includes("yürütme") || t.includes("yargı") || t.includes("idare") ||
        t.includes("hak ve hürriyet") || t.includes("kanun") || t.includes("mahkeme") ||
        t.includes("güncel bilgiler") || t.includes("uluslararası kuruluş") ||
        t.includes("tüzük") || t.includes("yönetmelik") || t.includes("bakanlar kurulu") ||
        t.includes("cumhurbaşkan") || t.includes("danıştay") || t.includes("sayıştay") ||
        t.includes("yargıtay") || t.includes("hsk")
    ) {
        return 'citizenship';
    }

    // Türkçe anahtar kelimeleri:
    if (
        t.includes("sözcük") || t.includes("cümle") || t.includes("paragraf") ||
        t.includes("ses bilgisi") || t.includes("yazım kural") || t.includes("noktalama") ||
        t.includes("fiil") || t.includes("öge") || t.includes("anlatım bozukluk") ||
        t.includes("sözel mantık") || t.includes("metin") || t.includes("yüklem") ||
        t.includes("özne") || t.includes("tamlama") || t.includes("edat") ||
        t.includes("bağlaç") || t.includes("ünlem") || t.includes("zarf") || t.includes("sıfat")
    ) {
        return 'turkish';
    }

    // Matematik anahtar kelimeleri:
    if (
        t.includes("matematik") || t.includes("geometri") || t.includes("üçgen") ||
        t.includes("fonksiyon") || t.includes("ebob") || t.includes("ekok") ||
        t.includes("üslü") || t.includes("köklü") || t.includes("çarpanlar") ||
        t.includes("oran - orantı") || t.includes("oran orantı") || t.includes("kesir") ||
        t.includes("sayısal mantık") || t.includes("basamak") || t.includes("bölünebilme") ||
        t.includes("denklem") || t.includes("eşitsizlik") || t.includes("mutlak değer") ||
        t.includes("problemleri") || t.includes("olasılık") || t.includes("küme")
    ) {
        return 'math';
    }

    // Coğrafya anahtar kelimeleri:
    if (
        t.includes("coğraf") || t.includes("fiziki") || t.includes("iklim") ||
        t.includes("bitki örtüsü") || t.includes("nüfus") || t.includes("tarım") ||
        t.includes("hayvancılık") || t.includes("maden") || t.includes("sanayi") ||
        t.includes("ulaşım") || t.includes("ticaret") || t.includes("turizm") ||
        t.includes("plato") || t.includes("akarsu") || t.includes("ova") ||
        t.includes("fay hattı") || t.includes("enlem") || t.includes("boylam") ||
        t.includes("karstik") || t.includes("vadi") || t.includes("delta") ||
        t.includes("körfez") || t.includes("havza") || t.includes("erozyon")
    ) {
        return 'geography';
    }
    
    return null;
};

/**
 * 🛡️ KESİN DERS TESPİTİ (Ironclad Subject Resolver):
 * Soru nesnesini (subject, topic, soru metni) derinlemesine inceleyerek
 * sorunun gerçekte hangi KPSS dersine ait olduğunu %100 kesinlikle belirler.
 * İlk kayıt anında yanlış derse kaydı kesin olarak engeller.
 */
export const resolveQuestionSubject = (
    q: { subject?: SubjectType; topic?: string; question?: string; [key: string]: any },
    fallbackSubject: SubjectType = 'geography'
): SubjectType => {
    if (!q) return fallbackSubject;

    // 1. Konu başlığından kesin tespit (En yüksek güvenilirlik)
    if (q.topic && q.topic.trim() && q.topic.trim() !== 'Genel') {
        const fromTopic = getSubjectFromTopic(q.topic);
        if (fromTopic) return fromTopic;
    }

    // 2. Soru metni içeriğinden anahtar kelime taraması (Eğer topic genel ise veya yetersizse)
    if (q.question && q.question.trim()) {
        const fromContent = getSubjectFromTopic(q.question);
        if (fromContent) return fromContent;
    }

    // 3. Soruda açıkça tanımlanmış subject varsa ve geçerliyse
    if (q.subject && ['geography', 'history', 'citizenship', 'turkish', 'math'].includes(q.subject)) {
        return q.subject;
    }

    return fallbackSubject;
};

export const mapTopicToNodeId = (topic: string): string => {
    const t = topic.toLocaleLowerCase('tr-TR');

    // 🛡️ DERS KONTROLÜ: Eğer konu Coğrafya dışı bir derse aitse ASLA Coğrafya düğümüne atama!
    const detected = getSubjectFromTopic(topic);
    if (detected && detected !== 'geography') {
        return '';
    }

    // ⚠️ "dağ" kelimesi "dağılma" (Dağılma Dönemi) ile ASLA karışmamalıdır
    const isMountain = /\bdağ(lar|ı|ın|da|dan)?\b/.test(t) || t.includes("dağları") || t.includes("dağlık");
    if (isMountain || t.includes("ova") || t.includes("plato") || t.includes("karstik") || t.includes("yer şekil") || t.includes("yerşekil")) return "fiziki_ozellikler";
    
    // "su" kelimesi de tek başına "sunu", "sulh" vb. kelimelerle karışmamalıdır
    const isWater = /\bsu(lar|yu|yun|da|dan)?\b/.test(t) || t.includes("akarsu") || t.includes("göl") || t.includes("toprak");
    if (isWater) return "fiziki_ozellikler";

    if (t.includes("iklim") || t.includes("bitki") || t.includes("yağış") || t.includes("sıcaklık")) return "iklim";
    if (t.includes("nüfus") || t.includes("göç") || t.includes("yerleşme")) return "nufus";
    if (t.includes("tarım") || t.includes("ürün")) return "tarim";
    if (t.includes("hayvan")) return "hayvancilik";
    if (t.includes("maden") || t.includes("enerji") || t.includes("santral") || t.includes("baraj") || t.includes("hes") || t.includes("petrol") || t.includes("doğalgaz") || t.includes("nükleer") || t.includes("demir") || t.includes("bor") || t.includes("krom") || t.includes("linyit")) return "madenler_enerji";
    if (t.includes("sanayi")) return "sanayi";
    if (t.includes("ulaşım") || t.includes("ticaret") || t.includes("turizm") || t.includes("yol") || t.includes("liman")) return "ulasim_ticaret";
    if (t.includes("bölge") || t.includes("gap") || t.includes("dap")) return "fiziki_ozellikler";
    if (t.includes("konum") || t.includes("enlem") || t.includes("boylam") || t.includes("sınır") || t.includes("koordinat") || t.includes("jeopolitik")) return "cografi_konum";
    if (t.includes("afet") || t.includes("deprem")) return "fiziki_ozellikler";
    return "fiziki_ozellikler"; 
};

export const HISTORY_BLUEPRINT_TO_NODE: Record<string, string> = {
    "İlk ve Orta Çağlarda Türk Dünyası": "u1",
    "Türklerin İslamiyet'i Kabulü ve İlk Türk-İslam Devletleri": "u2",
    "Yerleşme ve Devletleşme Sürecinde Selçuklu Türkiyesi": "u3",
    "Osmanlı Devleti Kültür ve Medeniyeti": "u4",
    "Beylikten Devlete Osmanlı Devleti": "u5",
    "Dünya Gücü Osmanlı": "u6",
    "Değişen Dünya Dengeleri Karşısında Osmanlı Devleti (XVII. Yüzyıl)": "u7",
    "Değişen Dünya Dengeleri Karşısında Osmanlı Siyaseti (XVIII. Yüzyıl)": "u8",
    "Değişen Dünya Dengeleri Karşısında Osmanlı Devleti (XVIII. Yüzyıl)": "u8",
    "En Uzun Yüzyıl (XIX. Yüzyılda Osmanlı Devleti)": "u9",
    "XIX. Yüzyıl Osmanlı Devleti Islahatları": "u10",
    "XX. Yüzyıl Başlarında Osmanlı Devleti ve Dünya": "u11",
    "Millî Mücadele Hazırlık Dönemi": "u12",
    "I. TBMM'nin Açılması, İç Ayaklanmalar ve Sevr Barış Antlaşması": "u13",
    "I. TBMM": "u13",
    "Millî Mücadele Muharebeler Dönemi": "u14",
    "Mustafa Kemal Atatürk'ün Hayatı": "u15",
    "Mustafa Kemal Atatürk": "u15",
    "Atatürk Dönemi İç Politika Gelişmeleri": "u16",
    "Atatürk İlkeleri": "u17",
    "Atatürk İnkılapları": "u18",
    "Cumhuriyet Dönemi Kültür ve Medeniyeti": "u19",
    "Atatürk Dönemi Dış Politikası": "u20",
    "Çağdaş Türk ve Dünya Tarihi": "u21"
};

// Tarih konusunu HISTORY_CHRONOLOGY ünitesine eşleştir
export const mapHistoryTopicToNodeId = (topic: string): string => {
    if (HISTORY_BLUEPRINT_TO_NODE[topic]) {
        console.log(`%c[mapHistoryTopicToNodeId] 🎯 Blueprint eşleşmesi: "${topic}" → "${HISTORY_BLUEPRINT_TO_NODE[topic]}"`, 'color:#10b981; font-weight:bold;');
        return HISTORY_BLUEPRINT_TO_NODE[topic];
    }

    // Akıllı normalleştirme (Türkçe karakterleri kaldırarak ve kelimeleri ayırarak eşleme yapar)
    const normalizeText = (str: string): string => {
        return str
            .toLocaleLowerCase('tr-TR')
            .replace(/ı/g, 'i')
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const t = normalizeText(topic);

    // 1. Adım: Tam veya kısmi string eşleşmesi (subTopics ve Unit isimleri içinde)
    for (const unit of HISTORY_CHRONOLOGY) {
        const normalizedUnitLabel = normalizeText(unit.label);
        const normalizedUnitShort = normalizeText(unit.shortLabel);
        
        if (normalizedUnitLabel.includes(t) || t.includes(normalizedUnitLabel) ||
            normalizedUnitShort.includes(t) || t.includes(normalizedUnitShort)) {
            console.log(`%c[mapHistoryTopicToNodeId] ✅ Ünite ismi eşleşmesi: "${topic}" → "${unit.id}" (${unit.label})`, 'color:#10b981');
            return unit.id;
        }

        for (const sub of unit.subTopics) {
            const normalizedSub = normalizeText(sub);
            if (normalizedSub.includes(t) || t.includes(normalizedSub)) {
                console.log(`%c[mapHistoryTopicToNodeId] ✅ Alt konu eşleşmesi: "${topic}" → "${unit.id}" (${sub})`, 'color:#10b981');
                return unit.id;
            }
        }
    }
    
    // 2. Adım: Genişletilmiş anahtar kelime eşleme (Keyword matching)
    const keywords: Record<string, string[]> = {
        u1: ['ilk turk', 'orta asya', 'hun', 'iskit', 'sakalar', 'kok turk', 'gokturk', 'uygur', 'hazar', 'turk goc', 'pecenek', 'kuman', 'kipcak', 'karluk', 'turgis', 'oguz', 'sibir', 'sabar', 'kirgiz', 'bulgar', 'avar', 'macar', 'tatar', 'kut ', 'ikili teskilat', 'kurultay', 'tore', 'saman', 'tengri', 'balbal', 'yug', 'kurgan'],
        u2: ['talas', 'karahan', 'gazne', 'buyuk selcuklu', 'misir', 'anadolu ilk', 'danismend', 'saltuk', 'mengucek', 'artuk', 'caka beyl', 'tolunoglu', 'ihsid', 'aksit', 'eyyubi', 'memluk', 'saray', 'hacib', 'saltanat', 'ikta', 'gulam', 'atabey', 'nizamiye', 'kutadgu', 'lugat', 'atabet', 'hikmet', 'sehname', 'farabi', 'ibni sina', 'biruni', 'gazali', 'harezmi'],
        u3: ['anadolu selcuklu', 'turkiye selcuklu', 'kosedag', 'miryokefalon', 'pasinler', 'malazgirt', 'ikinci beylik', 'osmanogullari', 'karamanogullari', 'germiyan', 'karesi', 'aydin', 'mentese', 'saruhan', 'hamit', 'candar', 'eretan', 'kadi burhan', 'dulkadir', 'ramazan'],
        u4: ['osmanli kultur', 'osmanli medeniyet', 'divanhumayun', 'sadrazam', 'vezir', 'defterdar', 'nisanci', 'kazasker', 'seyhulislam', 'seyfiye', 'ilmiye', 'kalemiye', 'timar', 'kapikulu', 'yeniceri', 'devsirme', 'salyaneli', 'salyanesiz', 'iltizam', 'mukataa', 'vakif', 'esham', 'lonca', 'musadere', 'enderun', 'medrese', 'darussifa', 'hat sanati', 'minyatur', 'tezhip', 'ebru', 'cini'],
        u5: ['osman bey', 'orhan bey', 'murat', 'yildirim', 'fetret', 'celebi mehmet', 'osmanli kurulus', 'koyunhisar', 'maltepe', 'palekanon', 'sazlidere', 'sirpsindigi', 'kosova', 'nigbolu', 'ankara savasi', 'varna'],
        u6: ['fatih', 'mehmet', 'fethi', 'bayezit', 'cem sultan', 'yavuz', 'selim', 'caldiran', 'turnadag', 'mercidabik', 'ridaniye', 'kanuni', 'suleyman', 'belgrat', 'mohac', 'viyana', 'preveze', 'cerbe', 'sokullu', 'inebahti'],
        u7: ['duraklama', 'xvii', '17', 'celali', 'suhte', 'osman', 'tarhuncu', 'koprulu', 'bucas', 'vasvar', 'karlofca'],
        u8: ['gerileme', 'xviii', '18', 'lale devri', 'patrona', 'kabakci', 'edirne olayi', 'prut', 'passarofca', 'belgrad', 'kucuk kaynarca', 'yas antlasmasi'],
        u9: ['xix', '19', 'en uzun', 'sirp isyani', 'rum isyani', 'edirne ant', 'misir sorunu', 'kavalali', 'hunkar iskelesi', 'balta limani', 'londra', 'kirim', 'paris ant', '93 harbi', 'ayastefanos', 'berlin ant'],
        u10: ['islahat', 'senediittifak', 'tanzimat', 'kanuniesasi', 'mesrutiyet', 'istibdat', '31 mart', 'darbe', 'genc osmanlilar', 'jon turkler', 'ittihat', 'osmanlicilik', 'islamcilik', 'turkculuk', 'baticilik', 'ademimerkeziyet'],
        u11: ['xx', '20', 'trablusgarp', 'usi', 'balkan', 'babiali', 'dunya savasi', 'cephe', 'kafkas', 'kanal', 'suriye', 'filistin', 'canakkale', 'irak', 'hicaz', 'yemen', 'gizli ant', 'wilson', 'mondros', 'paris baris', 'isgali', 'kuvayi milliye', 'cemiyet'],
        u12: ['samsun', 'havza', 'amasya genelgesi', 'erzurum', 'yerel kongre', 'balikesir', 'alasehir', 'sivas', 'basin', 'iradeimilliye', 'hakimiyetimilliye', 'amasya gorus', 'mebusan', 'misakimilli', 'misak i milli'],
        u13: ['tbmm nin acilmasi', 'birinci tbmm', 'i tbmm', 'ayaklanma', 'sevr'],
        u14: ['muharebe', 'dogu cephesi', 'gumru', 'guney cephesi', 'maras', 'antep', 'urfa', 'kadin kahraman', 'bati cephesi', 'inonu', 'kutahya', 'eskisehir', 'tekalifimilliye', 'sakarya', 'buyuk taarruz', 'baskomutanlik', 'mudanya', 'saltanat', 'lozan'],
        u15: ['hayati', 'kronolojisi', 'nutuk', 'eserleri'],
        u16: ['ic politika', 'terakkiperver', 'serbest cumhuriyet', 'menemen', 'seyh sait', 'suikast', 'vagon li', 'razgrad'],
        u17: ['ilke', 'cumhuriyetcilik', 'laiklik', 'halkcilik', 'milliyetcilik', 'devletcilik', 'inkilapcilik'],
        u18: ['inkilap', 'devrim', 'halifelik', 'tedrisat', 'ser iye', 'erkan i harbiye', 'tekke', 'zaviye', 'turbe', 'kilik kiyafet', 'sapka', 'takvim', 'saat', 'rakam', 'olcu', 'soyadi', 'kadin', 'mecelle', 'medeni kanun', 'harf devrimi', 'dil kurumu', 'tarih kurumu', 'universite', 'mektepleri', 'halkevleri', 'kabotaj', 'tarim', 'sanayi', 'iktisat', 'merkez bankasi', 'sumerbank', 'etibank', 'demiryollari'],
        u19: ['kultur ve medeniyet', 'kultur ve sanat', 'bilim', 'mimari', 'edebiyat', 'muzik', 'resim', 'tiyatro'],
        u20: ['dis politika', 'yabanci okullar', 'musul', 'nufus mubadelesi', 'borclar', 'patrik', 'bozkurt lotus', 'sadabat', 'balkan antanti', 'montro', 'hatay'],
        u21: ['cagdas', 'sscb', 'soguk savas', 'blok', 'nato', 'varsova', 'kore', 'vietnam', 'kesmir', 'afganistan', 'arap israil', 'petrol', 'iran irak', 'kibris', 'baglantisizlar', 'kuresellesen', 'korfez', 'avrupa birligi', 'tika']
    };

    for (const [unitId, kws] of Object.entries(keywords)) {
        for (const kw of kws) {
            const normalizedKw = normalizeText(kw);
            if (t.includes(normalizedKw) || normalizedKw.includes(t)) {
                console.log(`%c[mapHistoryTopicToNodeId] ⚠️ Keyword eşleşmesi: "${topic}" → "${unitId}" (Kelime: ${kw})`, 'color:#f59e0b');
                return unitId;
            }
        }
    }
    
    console.warn(`%c[mapHistoryTopicToNodeId] ❌ Eşleşme bulunamadı, varsayılan üniteye (u1) atandı: "${topic}" → "u1"`, 'color:#ef4444');
    return 'u1'; // Fallback
};

export const mapTurkishTopicToNodeId = (topic: string, subTopic?: string, text?: string): string => {
    const raw = `${topic || ''} ${subTopic || ''} ${text || ''}`.toLocaleLowerCase('tr-TR');
    const s = raw
        .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
        .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c');

    // 1. Fiil & Fiilimsiler (Öncelikli: Çatı, Eylemsi, Fiilimsi, Ek-Fiil)
    if (s.includes('fiilimsi') || s.includes('eylemsi') || s.includes('fiilde cati') || s.includes('fiil catisi') ||
        s.includes('ek-fiil') || s.includes('ek fiil') || s.includes('ek-eylem') || s.includes('ek eylem') ||
        s.includes('gecisli') || s.includes('gecissiz') || s.includes('ettirgen') || s.includes('oldurgan') ||
        s.includes('edilgen') || s.includes('etken fiil') || s.includes('fiiller ve fiilimsiler') || s.includes('cati bilgisi') ||
        s.includes('fiil cekimi') || s.includes('cekimli fiil')) {
        return 'trk_fiil';
    }

    // 2. Cümle Türleri (Yüklemin türüne, yapısına göre, basit, birleşik, sıralı, bağlı)
    if (s.includes('cumle tur') || s.includes('cumle cesit') || s.includes('yuklemin turune gore') ||
        s.includes('yapisina gore cumle') || s.includes('girisik birlesik') || s.includes('sirali cumle') || s.includes('bagli cumle')) {
        return 'trk_cumle_tur';
    }

    // 3. Cümlenin Ögeleri (Özne, Yüklem, Nesne, Tümleç, Öge dizilişi, Vurgu)
    if (s.includes('cumlenin ogeleri') || s.includes('oge dizilisi') || s.includes('belirtili nesne') ||
        s.includes('belirtisiz nesne') || s.includes('zarf tumleci') || s.includes('dolayli tumlec') ||
        s.includes('cumlede vurgu') || s.includes('soz obeklerinin cumledeki gorevi') || s.includes('cümle ögeleri')) {
        return 'trk_oge';
    }

    // 4. Sözcükte Yapı (Kök, Ek, Gövde, Türemiş, Birleşik sözcük, Biçim Bilgisi)
    if (s.includes('sozcukte yapi') || s.includes('yapi bilgisi') || s.includes('kok turleri') ||
        s.includes('isim ve fiil koku') || s.includes('cekim ekleri') || s.includes('yapim eki') ||
        s.includes('govdeden turemis') || s.includes('bicim bilgisi') || s.includes('kok ve ek')) {
        return 'trk_sozcuk_yapi';
    }

    // 5. Anlatım Bozuklukları
    if (s.includes('anlatim bozuk') || s.includes('anlatim bilgileri') || s.includes('duruluk') || s.includes('yalinlik')) {
        return 'trk_anlatim';
    }

    // 6. Sözel Mantık
    if (s.includes('sozel mantik') || s.includes('tablo ve oncul') || s.includes('matris')) {
        return 'trk_sozel_mantik';
    }

    // 7. Paragraf
    if (s.includes('paragraf') || s.includes('ana dusunce') || s.includes('yardimci dusunce') || s.includes('akisi bozan')) {
        return 'trk_paragraf';
    }

    // 8. Ses Bilgisi
    if (s.includes('ses bilgisi') || s.includes('unlu dusmesi') || s.includes('unsuz yumusamasi') || s.includes('unsuz sertlesmesi') || s.includes('ses olay')) {
        return 'trk_ses';
    }

    // 9. Yazım Kuralları
    if (s.includes('yazim kural') || s.includes('buyuk harf') || s.includes('ayri yazim') || s.includes('birlesik yazim')) {
        return 'trk_yazim';
    }

    // 10. Noktalama İşaretleri
    if (s.includes('noktalama') || s.includes('virgul') || s.includes('noktali virgul') || s.includes('iki nokta') || s.includes('kesme isareti')) {
        return 'trk_noktalama';
    }

    // 11. Cümlede Anlam
    if (s.includes('cumlede anlam') || s.includes('cumlenin anlami') || s.includes('uslup') || s.includes('icerik cumle') || s.includes('kesin yargi')) {
        return 'trk_cumle_anlam';
    }

    // 12. Sözcükte Anlam
    if (s.includes('sozcukte anlam') || s.includes('sozcugun ve sozun') || s.includes('deyim') || s.includes('atasozu') || s.includes('mecaz')) {
        return 'trk_sozcuk_anlam';
    }

    // 13. Sözcük Türleri (Sıfat, Zamir, Zarf, Edat, Bağlaç, Ünlem, İsim)
    if (s.includes('sozcuk tur') || s.includes('sifat') || s.includes('zamir') || s.includes('zarf') || s.includes('edat') || s.includes('baglac') || s.includes('unlem') || s.includes('tamlamalar') || s.includes('adil') || s.includes('belirtec')) {
        return 'trk_sozcuk_tur';
    }

    return TOPIC_TO_NODE_MAP[topic] || `trk_${slugify(topic)}`;
};

/** Ad, bir dersin resmî ünite adıysa o dersi döndürür (büyük/küçük harf duyarsız). */
export const unitSubjectOf = (name: string): SubjectType | undefined =>
    UNIT_TO_SUBJECT.get((name || '').trim().toLocaleLowerCase('tr-TR'));

const GENERIC_SUBTOPICS = new Set(['genel', 'coğrafya', 'tarih', 'vatandaşlık', 'türkçe', 'matematik', 'diğer']);

/**
 * Gelişim karnesine yazılacak alt konuyu süzer: boş, genel ("Coğrafya", "Genel"), ünitenin kendisiyle aynı ya da
 * BAŞKA BİR DERSİN RESMÎ ÜNİTE ADI olan alt konular atılır (yapay zekâ bazen "Türklerin İslamiyet'i Kabulü" ünite
 * adını coğrafya sorusunun alt konusuna yazıyordu). Anahtar kelime tahmini KULLANILMAZ: "1924 Anayasası" (tarih) ya da
 * "Maden İşleme Tesisleri" (içinde "işlem" geçiyor) gibi doğru alt konuları başka derse sanıp atıyordu.
 */
export const cleanSubTopic = (subTopic: string | null | undefined, subject: SubjectType, mainTopic: string): string | undefined => {
    const s = (subTopic || '').trim();
    if (!s) return undefined;
    const low = s.toLocaleLowerCase('tr-TR');
    if (GENERIC_SUBTOPICS.has(low) || low === mainTopic.toLocaleLowerCase('tr-TR')) return undefined;
    const unitSubject = UNIT_TO_SUBJECT.get(low);
    if (unitSubject && unitSubject !== subject) return undefined;
    return s;
};

export const getTargetNodeId = (mainTopic: string, subject: SubjectType, subTopic?: string, text?: string): string => {
    // 🛡️ DERS TESPİT GÜVENCESİ: Eğer mainTopic açıkça başka bir derse aitse, doğru dersi tespit et!
    const detectedSubject = getSubjectFromTopic(mainTopic) || subject;
    const finalSubject = (detectedSubject && detectedSubject !== subject) ? detectedSubject : subject;

    let targetNodeId = '';
    if (finalSubject === 'history') {
        targetNodeId = `history_${mapHistoryTopicToNodeId(mainTopic)}`;
    } else if (finalSubject === 'citizenship') {
        targetNodeId = TOPIC_TO_NODE_MAP[mainTopic] || `cit_${slugify(mainTopic)}`;
    } else if (finalSubject === 'turkish') {
        targetNodeId = mapTurkishTopicToNodeId(mainTopic, subTopic, text);
    } else if (finalSubject === 'math') {
        targetNodeId = TOPIC_TO_NODE_MAP[mainTopic] || `mat_${slugify(mainTopic)}`;
    } else if (TOPIC_TO_NODE_MAP[mainTopic]) {
        targetNodeId = TOPIC_TO_NODE_MAP[mainTopic];
    } else if (finalSubject === 'geography') {
        targetNodeId = mapTopicToNodeId(mainTopic);
    } else {
        targetNodeId = `${finalSubject}_${mainTopic.replace(/\s+/g, '_')}`;
    }
    return targetNodeId;
};

/**
 * TÜİK soruları (ortak havuz `global_questions_geography`, konu "TÜİK Verileri") nüfus, tarım, hayvancılık, enerji,
 * turizm… verisi sorar ama konu etiketi tek. Etiket hiçbir anahtar kelimeye uymadığı için `mapTopicToNodeId` varsayılanı
 * Fiziki Özellikler'e düşüyordu (2026-10-15: manda/tiftik keçisi/nüfus yoğunluğu soruları Fiziki düğümündeydi).
 * Konu TÜİK ise soru metninden resmî coğrafya ünitesi seçilir; değilse konu aynen döner.
 * Sıra önemli: tiftik sorusunda "sanayi" de geçiyor → hayvancılık önce.
 */
const TUIK_TOPIC_RE = /t[üu][iı̇]k/i;
export const isTuikTopic = (topic?: string): boolean => !!topic && TUIK_TOPIC_RE.test(topic.toLocaleLowerCase('tr-TR'));

export const tuikUnitFromText = (text: string): string => {
    const t = (text || '').toLocaleLowerCase('tr-TR');
    if (/hayvan|büyükbaş|küçükbaş|keçi|koyun|sığır|manda|kümes|tavuk|arıcılık|\bbal\b|ipek|balık|su ürünleri|süt\b|yumurta|tiftik|kırmızı et/.test(t)) return "Türkiye'de Hayvancılık";
    if (/enerji|elektrik|maden|petrol|doğal ?gaz|kömür|linyit|rüzgâr|rüzgar|güneş panel|jeotermal|santral|\bbor\b|krom|demir cevheri/.test(t)) return "Türkiye'de Madenler ve Enerji Kaynakları";
    if (/turist|turizm|ihracat|ithalat|dış ticaret|liman|havalimanı|hava ?yolu|demir ?yolu|yolcu|otoyol|kara ?yolu|ulaşım/.test(t)) return "Türkiye'de Ulaşım, Ticaret ve Turizm";
    if (/sanayi|fabrika|otomotiv|imalat/.test(t)) return "Türkiye'de Sanayi";
    if (/tarım|tarla|buğday|arpa|mısır|pamuk|fındık|\bçay\b|zeytin|üzüm|tütün|şeker pancarı|ayçiçeği|narenciye|turunçgil|muz|kayısı|incir|hasat|sera|ekili|rekolte|patates|kivi|haşhaş|kanola|elma|mercimek|fasulye|fıstık|soya|ürün|üretim/.test(t)) return "Türkiye'de Tarım"; // hayvan/enerji/sanayi yukarıda: kalan "üretim" soruları tarım
    if (/yağış|sıcaklık|iklim|kuraklık|orman/.test(t)) return "Türkiye'nin İklimi ve Bitki Örtüsü";
    // TÜİK sorularının çoğu nüfus verisi (nüfus, yoğunluk, göç, doğurganlık, yaş, kentleşme): varsayılan da nüfus
    return "Türkiye'de Nüfus ve Yerleşme";
};

/** Kayıt yollarında konu: TÜİK ise soru metninden ünite, değilse sorunun kendi konusu */
export const effectiveQuestionTopic = (q: { topic?: string; question?: string }, fallback = 'Genel'): string =>
    isTuikTopic(q.topic) ? tuikUnitFromText(q.question || '') : (q.topic || fallback);
